import Cookies from 'js-cookie';
import { ctxGpc } from 'utils/brijContext';
import { isPreviewMode } from 'utils/preview';

// SIG-114: Do Not Sell link (Part 2) and Global Privacy Control (Part 3).
// The browser-facing helpers below never throw: an opt-out read must never
// block an event, a registration or a page render.

export const DO_NOT_SELL_COOKIE = 'brijDoNotSell';
export const COOKIE_BANNER_DISMISSED_COOKIE = 'cookieBannerDismissed';
export const PRIVACY_OPT_OUT_SYNCED_KEY_PREFIX = 'brijPrivacyOptOutSynced:';
export const GPC_NOTICE_SHOWN_KEY = 'brijGpcNoticeShown';

export type PrivacyOptOutSource = 'dns_link' | 'gpc';

export type DoNotSellMarker = {
  at: string | null;
  source: string | null;
};

export type PrivacyOptOut = {
  // true = a GPC opt-out applies; false = the browser reported GPC off and
  // nothing asserted it; null = unknown.
  gpc: boolean | null;
  // true = the Do Not Sell marker is present; never false.
  doNotSell: true | null;
  active: boolean;
};

type ConsentValue = 'granted' | 'denied';

export type OptOutSnapshotLike = {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  allConsented?: boolean;
  gpc?: boolean | null;
  doNotSell?: true | null;
};

// GPC from the browser and from the page context (the Sec-GPC header seen by
// SSR): true if either says true, else false if either says false, else null.
// Non-boolean values are ignored.
export const mergeGpcSignals = (
  navigatorValue: unknown,
  contextValue: unknown
): boolean | null => {
  const signals = [navigatorValue, contextValue].filter(
    (value): value is boolean => typeof value === 'boolean'
  );
  if (signals.includes(true)) return true;
  if (signals.includes(false)) return false;
  return null;
};

// INVARIANT: a GPC or Do Not Sell opt-out denies the ad trio and clears
// allConsented. Analytics, functionality, personalization and security are
// left as they are. Returns the input unchanged when no opt-out applies.
export const applyOptOutInvariant = <T extends OptOutSnapshotLike>(
  snapshot: T
): T => {
  if (snapshot?.gpc !== true && snapshot?.doNotSell !== true) return snapshot;
  const clamped: T = {
    ...snapshot,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };
  if ('allConsented' in snapshot) clamped.allConsented = false;
  return clamped;
};

export const readNavigatorGpc = (): boolean | null => {
  try {
    if (typeof navigator === 'undefined' || !navigator) return null;
    const value = (navigator as Navigator & { globalPrivacyControl?: unknown })
      .globalPrivacyControl;
    return typeof value === 'boolean' ? value : null;
  } catch (error) {
    return null;
  }
};

const readContextGpc = (): boolean | null => {
  try {
    return ctxGpc();
  } catch (error) {
    return null;
  }
};

// Read on every render and event, so a blocked cookie jar is logged once.
let markerReadFailureLogged = false;

// Presence alone is the opt-out: an unreadable value still counts.
export const readDoNotSellMarker = (): DoNotSellMarker | null => {
  try {
    const raw = Cookies.get(DO_NOT_SELL_COOKIE);
    if (raw === undefined) return null;
    const marker: DoNotSellMarker = { at: null, source: null };
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        if (typeof parsed.at === 'string') marker.at = parsed.at;
        if (typeof parsed.source === 'string') marker.source = parsed.source;
      }
    } catch (parseError) {
      // Unreadable value - the marker still applies.
    }
    return marker;
  } catch (error) {
    if (!markerReadFailureLogged) {
      markerReadFailureLogged = true;
      console.error('DO NOT SELL MARKER READ FAILED: ', error);
    }
    return null;
  }
};

// Host-only, path / (the same scoping as the cookieConsent cookie). Never
// stored inside the cookieConsent JSON. An existing marker keeps its first
// timestamp. Returns whether the marker is present afterwards.
export const writeDoNotSellMarker = (): boolean => {
  try {
    if (!readDoNotSellMarker()) {
      Cookies.set(
        DO_NOT_SELL_COOKIE,
        JSON.stringify({ at: new Date().toISOString(), source: 'dns_link' }),
        { expires: 365 }
      );
    }
    const written = readDoNotSellMarker() !== null;
    if (!written) {
      console.warn('DO NOT SELL MARKER NOT STORED: ', DO_NOT_SELL_COOKIE);
    }
    return written;
  } catch (error) {
    console.error('DO NOT SELL MARKER WRITE FAILED: ', error);
    return false;
  }
};

// Only the explicit opt back in action removes the marker.
export const removeDoNotSellMarker = (): boolean => {
  try {
    Cookies.remove(DO_NOT_SELL_COOKIE);
    return readDoNotSellMarker() === null;
  } catch (error) {
    console.error('DO NOT SELL MARKER REMOVE FAILED: ', error);
    return false;
  }
};

// A standing Do Not Sell / GPC opt-out on the signed-in user's profile for
// the brand (profile.userPermissions[brandId].cookieConsent.meta.doNotSell).
// Never throws.
export const hasProfileDoNotSell = (
  personalDetails: unknown,
  brandId?: string | null
): boolean => {
  try {
    if (!brandId) return false;
    return (
      (personalDetails as any)?.profile?.userPermissions?.[brandId]
        ?.cookieConsent?.meta?.doNotSell?.value === true
    );
  } catch (error) {
    return false;
  }
};

export const getPrivacyOptOut = (): PrivacyOptOut => {
  let gpc: boolean | null = null;
  let doNotSell: true | null = null;
  try {
    gpc = mergeGpcSignals(readNavigatorGpc(), readContextGpc());
  } catch (error) {
    gpc = null;
  }
  try {
    doNotSell = readDoNotSellMarker() ? true : null;
  } catch (error) {
    doNotSell = null;
  }
  return { gpc, doNotSell, active: gpc === true || doNotSell === true };
};

// A dismissal is not consent: nothing is recorded and the regional default
// keeps applying.
export const isBannerDismissed = (): boolean => {
  try {
    return Cookies.get(COOKIE_BANNER_DISMISSED_COOKIE) === '1';
  } catch (error) {
    return false;
  }
};

export const writeBannerDismissed = (): void => {
  try {
    Cookies.set(COOKIE_BANNER_DISMISSED_COOKIE, '1', { expires: 30 });
  } catch (error) {
    console.error('COOKIE BANNER DISMISS WRITE FAILED: ', error);
  }
};

// Session flags. The in-memory copy keeps them working for the life of the
// page when sessionStorage is unavailable.
const memorySessionFlags = new Set<string>();

const readSessionFlag = (key: string): boolean => {
  try {
    if (window.sessionStorage.getItem(key) === '1') return true;
  } catch (error) {
    // sessionStorage unavailable - fall back to memory
  }
  return memorySessionFlags.has(key);
};

const writeSessionFlag = (key: string): void => {
  memorySessionFlags.add(key);
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (error) {
    // sessionStorage unavailable - memory only
  }
};

const clearSessionFlag = (key: string): void => {
  memorySessionFlags.delete(key);
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // sessionStorage unavailable - memory only
  }
};

// Keyed by brand AND signed-in user, so another user signing in on the same
// tab still gets their own profile write for that brand.
const privacyOptOutSyncedKey = (brandId: string, userId: string): string =>
  `${PRIVACY_OPT_OUT_SYNCED_KEY_PREFIX}${brandId}:${userId}`;

export const isPrivacyOptOutSynced = (
  brandId: string,
  userId: string
): boolean => readSessionFlag(privacyOptOutSyncedKey(brandId, userId));

export const markPrivacyOptOutSynced = (
  brandId: string,
  userId: string
): void => writeSessionFlag(privacyOptOutSyncedKey(brandId, userId));

export const clearPrivacyOptOutSynced = (
  brandId: string,
  userId: string
): void => clearSessionFlag(privacyOptOutSyncedKey(brandId, userId));

export const isGpcNoticeShown = (): boolean =>
  readSessionFlag(GPC_NOTICE_SHOWN_KEY);

export const markGpcNoticeShown = (): void =>
  writeSessionFlag(GPC_NOTICE_SHOWN_KEY);

// Admin registration, preview and any embedded frame act for someone other
// than the visitor in this browser: no profile writes from there. When the
// check itself fails, assume a frame.
export const isAdminPreviewOrEmbedded = (): boolean => {
  try {
    return (
      isPreviewMode() ||
      (window as any).mode === 'adminRegistration' ||
      window.self !== window.top
    );
  } catch (error) {
    return true;
  }
};
